import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import {
  Calendar,
  MapPin,
  Shield,
  MessageSquare,
  Star,
  Camera,
  ChevronLeft,
  Loader2,
  AlertCircle,
  Check,
  X,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import ReviewModal from "../components/ReviewModal";

const statusConfig = {
  requested: { label: "Solicitado", className: "status-pending", color: "bg-yellow-500" },
  confirmed: { label: "Confirmado", className: "status-confirmed", color: "bg-green-500" },
  active: { label: "En Curso", className: "status-active", color: "bg-blue-500" },
  completed: { label: "Completado", className: "status-completed", color: "bg-purple-500" },
  canceled: { label: "Cancelado", className: "status-canceled", color: "bg-red-500" },
};

export default function TripDetailsPage() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);

  useEffect(() => {
    fetchBooking();
    
    // Check for payment success
    const payment = searchParams.get("payment");
    const sessionId = searchParams.get("session_id");
    
    if (payment === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [bookingId, searchParams]);

  const fetchBooking = async () => {
    try {
      const response = await api.get(`/bookings/${bookingId}`);
      setBooking(response.data);
      // check if already reviewed
      if (response.data?.status === "completed") {
        try {
          const r = await api.get(`/reviews/booking/${bookingId}`);
          setHasReviewed(!!r.data?.reviewed);
        } catch {
          /* ignore */
        }
      }
    } catch (error) {
      console.error("Error fetching booking:", error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info("Por favor verifica tu email para confirmación de pago");
      return;
    }

    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        toast.success("¡Pago completado exitosamente!");
        fetchBooking(); // Refresh booking data
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Estás seguro de que deseas cancelar esta reservación?")) {
      return;
    }

    try {
      await api.put(`/bookings/${bookingId}/status`, { status: "canceled" });
      toast.success("Reservación cancelada");
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cancelar");
    }
  };

  const handleSubmitReview = () => setReviewModalOpen(true);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Viaje no encontrado</h2>
        <Button onClick={() => navigate("/trips")} className="btn-primary">
          Ver mis viajes
        </Button>
      </div>
    );
  }

  const status = statusConfig[booking.status] || statusConfig.requested;
  const isGuest = booking.guest_id === user?.user_id;
  const canCancel = ["requested", "confirmed"].includes(booking.status);
  const canReview = booking.status === "completed";

  return (
    <div className="min-h-screen bg-sand pb-8" data-testid="trip-details-page">
      <div className="container-main py-6">
        {/* Back Button */}
        <button
          onClick={() => navigate("/trips")}
          className="inline-flex items-center text-gray-600 hover:text-terracotta mb-6"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Volver a mis viajes
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Header */}
            <div className="card-base">
              <div className="flex items-center justify-between mb-4">
                <Badge className={`${status.className} text-sm px-3 py-1`}>
                  {status.label}
                </Badge>
                {booking.payment_status === "paid" && (
                  <Badge className="bg-green-100 text-green-700">
                    <Check className="w-3 h-3 mr-1" />
                    Pagado
                  </Badge>
                )}
              </div>

              {/* Vehicle Info */}
              <div className="flex gap-4">
                <img
                  src={booking.vehicle?.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=200"}
                  alt={`${booking.vehicle?.make} ${booking.vehicle?.model}`}
                  className="w-32 h-32 object-cover rounded-xl"
                />
                <div>
                  <h1 className="text-2xl font-heading font-bold text-agave">
                    {booking.vehicle?.make} {booking.vehicle?.model} {booking.vehicle?.year}
                  </h1>
                  <p className="text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" />
                    {booking.vehicle?.location_city}
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    Reservación: {booking.booking_id}
                  </p>
                </div>
              </div>
            </div>

            {/* Trip Dates */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-terracotta" />
                Fechas del Viaje
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-sand rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Recogida</p>
                  <p className="font-semibold">
                    {format(new Date(booking.pickup_date), "EEEE, dd MMMM yyyy", { locale: es })}
                  </p>
                </div>
                <div className="bg-sand rounded-xl p-4">
                  <p className="text-sm text-gray-500 mb-1">Devolución</p>
                  <p className="font-semibold">
                    {format(new Date(booking.return_date), "EEEE, dd MMMM yyyy", { locale: es })}
                  </p>
                </div>
              </div>

              {/* Pickup Location */}
              <div className="mt-4 bg-sand rounded-xl p-4">
                <p className="text-sm text-gray-500 mb-1">Ubicación</p>
                <p className="font-medium">
                  {booking.pickup_type === "delivery"
                    ? `Entrega en: ${booking.delivery_address}`
                    : booking.vehicle?.location_address}
                </p>
              </div>
            </div>

            {/* Protection Plan */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-terracotta" />
                Plan de Protección
              </h3>
              <div className="bg-sand rounded-xl p-4">
                <p className="font-semibold capitalize">{booking.protection_plan}</p>
                <p className="text-sm text-gray-500 mt-1">
                  Incluye asistencia en carretera y soporte 24/7
                </p>
              </div>
            </div>

            {/* Host/Guest Info — Privacy: NEVER expose phone/email of the
                other party. Direct contact is always routed through the
                in-app messaging system (`/inbox/{booking_id}`). The
                "Mensaje" CTA is the ONLY way to reach the other party. */}
            <div className="card-base">
              <h3 className="font-heading font-semibold text-lg mb-4">
                {isGuest ? "Tu Anfitrión" : "Tu Huésped"}
              </h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14">
                    <AvatarImage src={isGuest ? booking.host?.picture : booking.guest?.picture} />
                    <AvatarFallback className="bg-terracotta text-white">
                      {(isGuest ? booking.host?.name : booking.guest?.name)?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold">
                      {isGuest ? booking.host?.name : booking.guest?.name}
                    </p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      Por privacidad, la comunicación se mantiene dentro de RivoNes.
                    </p>
                  </div>
                </div>
                <Button
                  variant="default"
                  onClick={() => navigate(`/inbox/${bookingId}`)}
                  data-testid="message-btn"
                  className="bg-terracotta hover:bg-terracotta/90"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  {isGuest ? "Mensaje al anfitrión" : "Mensaje al huésped"}
                </Button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {canCancel && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  onClick={handleCancel}
                  data-testid="cancel-booking-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar Reservación
                </Button>
              )}

              {canReview && !hasReviewed && (
                <>
                  <Button
                    className="btn-primary"
                    onClick={() => setReviewModalOpen(true)}
                    data-testid="write-review-btn"
                  >
                    <Star className="w-4 h-4 mr-2" />
                    Escribir Reseña
                  </Button>
                  <ReviewModal
                    open={reviewModalOpen}
                    onOpenChange={setReviewModalOpen}
                    booking={booking}
                    onSubmitted={() => {
                      setHasReviewed(true);
                      fetchBooking();
                    }}
                  />
                </>
              )}
              {canReview && hasReviewed && (
                <Badge className="bg-green-100 text-green-700 self-center" data-testid="review-submitted-badge">
                  <Check className="w-3 h-3 mr-1" />
                  Reseña enviada
                </Badge>
              )}
            </div>
          </div>

          {/* Price Summary */}
          <div className="lg:col-span-1">
            <div className="card-base sticky top-24">
              <h3 className="font-heading font-semibold text-lg mb-4">
                Resumen de Precio
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Precio base</span>
                  <span>${booking.base_price?.toLocaleString()}</span>
                </div>

                {booking.delivery_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Entrega a domicilio</span>
                    <span>${booking.delivery_fee?.toLocaleString()}</span>
                  </div>
                )}

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Protección</span>
                  <span>${booking.protection_fee?.toLocaleString()}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tarifa de servicio</span>
                  <span>${booking.platform_fee?.toLocaleString()}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">IVA</span>
                  <span>${booking.tax?.toLocaleString()}</span>
                </div>

                <Separator />

                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span className="text-terracotta">
                    ${booking.total_price?.toLocaleString()} MXN
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
